import {
  EMPTY_TABLE_STATE,
  type Filter,
  type FilterCondition,
  type TableState,
} from '@crm/contracts';
import type { PipelineView } from './client.ts';
import { andFilter } from './list-view.ts';

/**
 * The Jobs list's two quick filters (RELAY-953), kept INSIDE the view's filter (RELAY-1038) so
 * a saved view or a link carries them like any other condition:
 *
 *   mine     — jobs the viewer is on: recruiter, coordinator or account manager, or holding a
 *              function on the job's project (Settings → Project settings) — one
 *              `involvedId eq <me>` condition. A view saved before functions existed carries the
 *              old OR group over the three people fields; it is read as Mine too, and written
 *              back as `involvedId`;
 *   openOnly — jobs not in a terminal Job Status, read from the pipeline so a status an admin
 *              adds or renames is handled without a code change.
 *
 * `splitJobFilter` reads them back out of a stored filter, leaving the flat conditions the
 * filter bar edits; `joinJobFilter` puts the three back together.
 */
const PEOPLE = ['recruiterId', 'coordinatorId', 'accountManagerId'] as const;

export function mineFilter(userId: string): Filter {
  return { field: 'involvedId', op: 'eq', value: userId };
}

function terminalIds(pipeline: PipelineView): string[] {
  return pipeline.stages
    .filter((s) => s.isTerminal)
    .map((s) => s.id)
    .sort();
}

function openFilter(pipeline: PipelineView): FilterCondition | undefined {
  const ids = terminalIds(pipeline);
  return ids.length ? { field: 'stageId', op: 'not_in', value: ids } : undefined;
}

const isMine = (f: Filter, userId: string) =>
  ('field' in f && f.field === 'involvedId' && f.op === 'eq' && f.value === userId) ||
  ('or' in f &&
    f.or.length === PEOPLE.length &&
    f.or.every(
      (c, i) => 'field' in c && c.field === PEOPLE[i] && c.op === 'eq' && c.value === userId,
    ));

const isOpen = (f: Filter, pipeline: PipelineView) =>
  'field' in f &&
  f.field === 'stageId' &&
  f.op === 'not_in' &&
  Array.isArray(f.value) &&
  JSON.stringify([...(f.value as string[])].sort()) === JSON.stringify(terminalIds(pipeline));

export interface JobQuickFilters {
  mine: boolean;
  openOnly: boolean;
  /** What the filter bar shows and edits. */
  conditions: FilterCondition[];
  /** Anything else (a group from someone else's link): kept, applied, not shown. */
  rest: Filter[];
}

export function splitJobFilter(
  filter: Filter | undefined,
  userId: string,
  pipeline: PipelineView,
): JobQuickFilters {
  const parts = !filter ? [] : 'and' in filter ? filter.and : [filter];
  const out: JobQuickFilters = { mine: false, openOnly: false, conditions: [], rest: [] };
  for (const p of parts) {
    if (isMine(p, userId)) out.mine = true;
    else if (isOpen(p, pipeline)) out.openOnly = true;
    else if ('field' in p) out.conditions.push(p);
    else out.rest.push(p);
  }
  return out;
}

export function joinJobFilter(
  q: JobQuickFilters,
  userId: string,
  pipeline: PipelineView,
): Filter | undefined {
  const open = q.openOnly ? openFilter(pipeline) : undefined;
  return andFilter([
    ...(q.mine ? [mineFilter(userId)] : []),
    ...(open ? [open] : []),
    ...q.conditions,
    ...q.rest,
  ]);
}

/** Where a recruiter lands with nothing chosen: my open jobs. */
export function myOpenJobs(userId: string, pipeline: PipelineView): TableState {
  const filter = joinJobFilter(
    { mine: true, openOnly: true, conditions: [], rest: [] },
    userId,
    pipeline,
  );
  return { ...EMPTY_TABLE_STATE, ...(filter ? { filter } : {}) };
}
